import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import type { DropdownValue } from '@/components/core-components/StanDropdown/StanDropdown.types';
import { getLocalDateString, minutesToTimeString, parseApiTimeToMinutes, TIME_OPTIONS } from '@/lib/utils/timeUtils';
import { useAvailability } from '../queries/useAvailability';

interface UseRescheduleModalParams {
    bookingId: string;
    isOpen: boolean;
    initialStartDateTime?: string;
}

export function useRescheduleModal({ bookingId, isOpen, initialStartDateTime }: UseRescheduleModalParams) {
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
    const [calendarMonth, setCalendarMonth] = useState<Date>(new Date());
    const [startTimeMinutes, setStartTimeMinutes] = useState<number | undefined>(undefined);
    const [endTimeMinutes, setEndTimeMinutes] = useState<number | undefined>(undefined);
    const [reason, setReason] = useState('');

    // Initialize with current appointment date (but not time) when modal opens
    useEffect(() => {
        if (isOpen && initialStartDateTime) {
            try {
                const startDate = new Date(initialStartDateTime);
                setSelectedDate(startDate);
                setCalendarMonth(startDate);
                setReason('');
                // Don't pre-select times - user must choose from available slots
            } catch (error) {
                console.error('Failed to parse initial date:', error);
                setCalendarMonth(new Date());
                setSelectedDate(undefined);
                setReason('');
            }
        } else if (isOpen) {
            setCalendarMonth(new Date());
            setSelectedDate(undefined);
            setReason('');
        }
    }, [isOpen, initialStartDateTime]);

    // Fetch availability for the current calendar month
    const { data: availabilityData } = useAvailability({
        bookingId,
        month: calendarMonth,
        enabled: isOpen && !!bookingId,
    });

    // Get available slots for the selected date
    const availableSlotsForDate = useMemo(() => {
        if (!selectedDate || !availabilityData) return [];
        const dateString = getLocalDateString(selectedDate);
        return availabilityData.available_slots[dateString] || [];
    }, [selectedDate, availabilityData]);

    // Get available start times as minutes from midnight
    const availableStartTimes = useMemo(() => {
        return availableSlotsForDate
            .map(slot => parseApiTimeToMinutes(slot.stime, slot.ampm))
            .filter(time => time !== -1);
    }, [availableSlotsForDate]);

    // Filter time options
    const filteredStartTimeOptions = useMemo(() => {
        if (!selectedDate || availableStartTimes.length === 0) return TIME_OPTIONS;

        return TIME_OPTIONS.filter(option => availableStartTimes.includes(Number(option.value)));
    }, [selectedDate, availableStartTimes]);

    const filteredEndTimeOptions = useMemo(() => {
        // If no date selected, no start time selected, or no availability data
        if (!selectedDate || startTimeMinutes === undefined || availableSlotsForDate.length === 0) {
            return [];
        }

        // Find the starting slot index
        const startIndex = availableSlotsForDate.findIndex(slot => {
            const slotStartMinutes = parseApiTimeToMinutes(slot.stime, slot.ampm);
            return slotStartMinutes === startTimeMinutes;
        });

        // If no matching start slot found, show all times after start (for keeping original time)
        if (startIndex === -1) {
            return TIME_OPTIONS.filter(option => Number(option.value) > startTimeMinutes);
        }

        // Get consecutive slots starting from the selected time
        const slotsFromStart = availableSlotsForDate.slice(startIndex);

        // Build consecutive slots (max 8 hours like Vue)
        const consecutiveSlots = slotsFromStart.reduce((acc: typeof availableSlotsForDate, slot) => {
            const slotStartMinutes = parseApiTimeToMinutes(slot.stime, slot.ampm);
            const hoursSinceStart = (slotStartMinutes - startTimeMinutes) / 60;

            // Check if this slot is consecutive with previous slots
            if (acc.length === 0) {
                acc.push(slot);
            } else {
                const lastSlot = acc[acc.length - 1];
                if (lastSlot) {
                    // Parse last slot's end time
                    const lastEndParts = lastSlot.endTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
                    if (lastEndParts) {
                        let lastEndHour = parseInt(lastEndParts[1] || '0', 10);
                        const lastEndMin = parseInt(lastEndParts[2] || '0', 10);
                        const lastEndPeriod = lastEndParts[3]?.toUpperCase();

                        if (lastEndPeriod === 'PM' && lastEndHour !== 12) {
                            lastEndHour += 12;
                        } else if (lastEndPeriod === 'AM' && lastEndHour === 12) {
                            lastEndHour = 0;
                        }

                        const lastEndMinutes = lastEndHour * 60 + lastEndMin;

                        // Check if current slot starts at or before the last slot ends (consecutive)
                        if (hoursSinceStart <= 8 && slotStartMinutes <= lastEndMinutes) {
                            acc.push(slot);
                        }
                    }
                }
            }
            return acc;
        }, []);

        // Create end time options from consecutive slots with duration labels
        return consecutiveSlots
            .map(slot => {
                // Parse end time
                const endParts = slot.endTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
                if (!endParts) return null;

                let endHour = parseInt(endParts[1] || '0', 10);
                const endMin = parseInt(endParts[2] || '0', 10);
                const endPeriod = endParts[3]?.toUpperCase();

                if (endPeriod === 'PM' && endHour !== 12) {
                    endHour += 12;
                } else if (endPeriod === 'AM' && endHour === 12) {
                    endHour = 0;
                }

                const endMinutes = endHour * 60 + endMin;
                const durationMinutes = endMinutes - startTimeMinutes;

                return {
                    label: `${slot.endTime} (${durationMinutes}min)`,
                    value: String(endMinutes),
                };
            })
            .filter((opt): opt is DropdownValue => opt !== null && Number(opt.value) > startTimeMinutes);
    }, [startTimeMinutes, selectedDate, availableSlotsForDate]);

    // Current time values
    const currentStartTime = useMemo(() => {
        if (startTimeMinutes === undefined) return undefined;
        const option = TIME_OPTIONS.find(opt => opt.value === String(startTimeMinutes));
        return option;
    }, [startTimeMinutes]);

    const currentEndTime = useMemo(() => {
        if (endTimeMinutes === undefined) return undefined;
        // For end time, look in the filtered options with duration labels
        const option = filteredEndTimeOptions.find(opt => opt.value === String(endTimeMinutes));
        return option;
    }, [endTimeMinutes, filteredEndTimeOptions]);

    // Disabled dates for calendar
    const disabledDates = useMemo(() => {
        if (!availabilityData) {
            return [() => true]; // Disable all dates until data loads
        }

        return [
            { before: new Date() },
            (date: Date) => {
                const dateString = getLocalDateString(date);
                const slots = availabilityData.available_slots?.[dateString];
                return !slots || slots.length === 0;
            },
        ];
    }, [availabilityData]);

    const isValid = selectedDate && startTimeMinutes !== undefined && endTimeMinutes !== undefined;

    const handleStartTimeSelect = (value: DropdownValue) => {
        const newStartTime = Number(value.value);
        setStartTimeMinutes(newStartTime);

        // Find the matching slot and auto-set end time to the slot's end time (like Vue)
        const matchingSlot = availableSlotsForDate.find(slot => {
            const slotStartMinutes = parseApiTimeToMinutes(slot.stime, slot.ampm);
            return slotStartMinutes === newStartTime;
        });

        if (matchingSlot) {
            // Parse slot end time
            const endParts = matchingSlot.endTime.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
            if (endParts) {
                let endHour = parseInt(endParts[1] || '0', 10);
                const endMin = parseInt(endParts[2] || '0', 10);
                const endPeriod = endParts[3]?.toUpperCase();

                if (endPeriod === 'PM' && endHour !== 12) {
                    endHour += 12;
                } else if (endPeriod === 'AM' && endHour === 12) {
                    endHour = 0;
                }

                setEndTimeMinutes(endHour * 60 + endMin);
            }
        }
    };

    const handleEndTimeSelect = (value: DropdownValue) => {
        setEndTimeMinutes(Number(value.value));
    };

    const handleDateSelect = (date: Date | undefined, closeMenu: () => void) => {
        setSelectedDate(date);
        closeMenu();
    };

    const handleSave = async (
        onSave: (data: { date: string; startTime: string; endTime: string; reason?: string }) => Promise<void>
    ) => {
        if (!isValid || !selectedDate || startTimeMinutes === undefined || endTimeMinutes === undefined) return;

        const dateString = getLocalDateString(selectedDate);

        await onSave({
            date: dateString,
            startTime: minutesToTimeString(startTimeMinutes),
            endTime: minutesToTimeString(endTimeMinutes),
            reason: reason || undefined,
        });
    };

    const formattedDate = selectedDate ? format(selectedDate, 'MMM dd, yyyy') : 'Select Date';

    return {
        // State
        selectedDate,
        calendarMonth,
        startTimeMinutes,
        endTimeMinutes,
        reason,

        // Derived state
        currentStartTime,
        currentEndTime,
        filteredStartTimeOptions,
        filteredEndTimeOptions,
        disabledDates,
        isValid,
        formattedDate,

        // Setters
        setCalendarMonth,
        setReason,

        // Handlers
        handleStartTimeSelect,
        handleEndTimeSelect,
        handleDateSelect,
        handleSave,
    };
}
